import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Loader2, CheckCircle2, Gift } from "lucide-react";
import { useLang } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRICES = { low: 18, mid: 45, premium: 100 };

const initial = {
  full_name: "",
  phone: "",
  email: "",
  address: "",
  city: "",
  postal_code: "",
  quantity: 1,
  notes: "",
  is_gift: false,
  gift_recipient_name: "",
  gift_message: "",
};

export const OrderDialog = ({ open, onOpenChange, productType }) => {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const product = t(`products.${productType || "premium"}`);
  const price = PRICES[productType] || PRICES.premium;
  const total = (price * Math.max(1, Number(form.quantity || 1))).toFixed(2);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const reset = () => {
    setForm(initial);
    setSuccess(null);
  };

  const handleClose = (val) => {
    onOpenChange(val);
    if (!val) setTimeout(reset, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.address || !form.city) {
      toast.error(t("order.errors.required"));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        email: form.email || null,
        postal_code: form.postal_code || null,
        notes: form.notes || null,
        gift_recipient_name: form.is_gift ? form.gift_recipient_name || null : null,
        gift_message: form.is_gift ? form.gift_message || null : null,
        quantity: Number(form.quantity || 1),
        product_type: productType,
      };
      const { data } = await axios.post(`${API}/orders`, payload);
      setSuccess(data);
      toast.success(t("order.success"));
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        t("order.errors.generic");
      toast.error(typeof msg === "string" ? msg : t("order.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  const f = t("order.fields");

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="order-dialog"
        className="max-w-xl bg-black border-[color:var(--goldenelia-gold)]/40 text-[color:var(--goldenelia-cream)] p-0 overflow-hidden max-h-[90vh] overflow-y-auto"
      >
        <div className="p-8">
          <DialogHeader>
            <DialogTitle
              data-testid="order-dialog-title"
              className="font-serif-luxury text-3xl gold-gradient-text"
            >
              {success ? t("order.thanks") : t("order.title")}
            </DialogTitle>
            <DialogDescription className="font-body text-[color:var(--goldenelia-cream)]/70 text-sm">
              {success ? t("order.subDone") : `${product.name} — €${price}`}
            </DialogDescription>
          </DialogHeader>

          {success ? (
            <div data-testid="order-success" className="mt-6 text-center py-6">
              <CheckCircle2 size={56} className="text-[color:var(--goldenelia-gold)] mx-auto mb-4" />
              <p className="font-serif-luxury text-lg text-[color:var(--goldenelia-cream)] mb-2">
                {t("order.orderNo")} #{(success.id || "").slice(0, 8).toUpperCase()}
              </p>
              <p className="font-body text-sm text-[color:var(--goldenelia-cream)]/70 mb-6">
                {t("order.total")}: <span className="text-gold-bright">€{success.total_price}</span>
              </p>
              <button
                data-testid="order-close-btn"
                className="btn-ghost-gold"
                onClick={() => handleClose(false)}
              >
                {t("order.close")}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label={f.name} name="full_name" value={form.full_name} onChange={handleChange} testid="order-name" />
                <Field label={f.phone} name="phone" value={form.phone} onChange={handleChange} testid="order-phone" />
              </div>
              <Field label={f.email} name="email" type="email" value={form.email} onChange={handleChange} testid="order-email" />
              <Field label={f.address} name="address" value={form.address} onChange={handleChange} testid="order-address" />
              <div className="grid grid-cols-3 gap-3">
                <Field label={f.city} name="city" value={form.city} onChange={handleChange} testid="order-city" className="col-span-2" />
                <Field label={f.postal} name="postal_code" value={form.postal_code} onChange={handleChange} testid="order-postal" />
              </div>
              <Field label={f.quantity} name="quantity" type="number" min={1} max={50} value={form.quantity} onChange={handleChange} testid="order-quantity" />

              {/* Gift toggle + fields */}
              <div className="border border-[color:var(--goldenelia-gold)]/25 p-4 bg-[color:var(--goldenelia-gold)]/5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    data-testid="order-is-gift"
                    type="checkbox"
                    name="is_gift"
                    checked={form.is_gift}
                    onChange={handleChange}
                    className="mt-1 accent-[color:var(--goldenelia-gold)] w-4 h-4"
                  />
                  <span className="flex-1">
                    <span className="flex items-center gap-2 font-display text-[11px] tracking-[0.25em] uppercase text-[color:var(--goldenelia-gold-bright)]">
                      <Gift size={14} /> {t("order.isGift")}
                    </span>
                    <span className="block font-body text-xs text-[color:var(--goldenelia-cream)]/65 mt-1">
                      {t("order.giftHelp")}
                    </span>
                  </span>
                </label>

                {form.is_gift && (
                  <div className="mt-4 space-y-3">
                    <Field
                      label={f.recipient}
                      name="gift_recipient_name"
                      value={form.gift_recipient_name}
                      onChange={handleChange}
                      testid="order-recipient"
                    />
                    <Field
                      label={f.giftMsg}
                      name="gift_message"
                      value={form.gift_message}
                      onChange={handleChange}
                      testid="order-gift-msg"
                      textarea
                    />
                  </div>
                )}
              </div>

              <Field label={f.notes} name="notes" value={form.notes} onChange={handleChange} testid="order-notes" textarea />

              <div className="flex items-center justify-between border-t border-[color:var(--goldenelia-gold)]/20 pt-5">
                <div>
                  <p className="font-display text-[10px] tracking-[0.3em] text-gold uppercase">
                    {t("order.total")}
                  </p>
                  <p
                    data-testid="order-total"
                    className="font-serif-luxury text-3xl gold-gradient-text"
                  >
                    €{total}
                  </p>
                </div>
                <button
                  data-testid="order-submit-btn"
                  type="submit"
                  className="btn-ghost-gold"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="animate-spin" size={16} /> : null}
                  {loading ? t("order.sending") : t("order.submit")}
                </button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, name, value, onChange, type = "text", testid, textarea, className = "", ...rest }) => (
  <label className={`block ${className}`}>
    <span className="block font-display text-[10px] tracking-[0.3em] uppercase text-[color:var(--goldenelia-gold)]/85 mb-2">
      {label}
    </span>
    {textarea ? (
      <textarea
        data-testid={testid}
        name={name}
        value={value}
        onChange={onChange}
        rows={3}
        className="w-full px-3 py-2 border bg-transparent text-sm focus:outline-none"
      />
    ) : (
      <input
        data-testid={testid}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        className="w-full px-3 py-2 border bg-transparent text-sm focus:outline-none"
        {...rest}
      />
    )}
  </label>
);

export default OrderDialog;
